import { createMuiTheme } from "@material-ui/core";
import { fade } from "@material-ui/core/styles/colorManipulator";

const levels = [
  "50",
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
];

function createColorLevels(hue, saturation, lightness, step) {
  const result = {};
  let curLightness = lightness;
  levels.forEach((level) => {
    result[level] = `hsl(${hue}, ${saturation}, ${curLightness}%)`;
    curLightness += step;
  });
  return result;
}

function createBgLevels(dark, contrast) {
  return dark
    ? createColorLevels(0, "0%", 12, contrast * 0.08)
    : createColorLevels(0, "0%", 98, contrast * -0.08);
}

function createBorderLevels(dark, contrast) {
  return dark
    ? createColorLevels(0, "0%", 12, contrast * 0.2)
    : createColorLevels(0, "0%", 98, contrast * -0.2);
}

function createGreyLevels(dark, contrast) {
  return dark
    ? createColorLevels(0, "0%", 12, contrast * 0.5)
    : createColorLevels(0, "0%", 98, contrast * -0.5);
}

function createTheme(options = {}) {
  const { primaryColor, secondaryColor, dark, contrast } = options;

  const type = dark ? "dark" : "light";
  const bg = createBgLevels(dark, contrast);
  const border = createBorderLevels(dark, contrast);
  const grey = createGreyLevels(dark, contrast);
  const high = dark ? "rgb(26, 26, 26)" : "#fff";
  const low = dark ? "#fff" : "rgb(26, 26, 26)";
  const text = dark ? "#eee" : "#444";

  return createMuiTheme({
    spacing: (n) => n * 4,
    palette: {
      type,
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: secondaryColor,
      },
      custom: {
        bg,
        border,
        grey,
        high,
        low,
      },
    },
    props: {
      MuiButtonBase: {
        disableRipple: true,
      },
      MuiButton: {
        disableElevation: true,
        disableFocusRipple: true,
        disableRipple: true,
      },
      MuiButtonGroup: {
        disableElevation: true,
        disableFocusRipple: true,
        disableRipple: true,
      },
      MuiCheckbox: {
        disableRipple: true,
        color: "primary",
      },
      MuiRadio: {
        disableRipple: true,
        color: "primary",
      },
      MuiSwitch: {
        disableRipple: true,
        color: "primary",
      },
      MuiSelect: {
        MenuProps: {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          getContentAnchorEl: null,
        },
      },
    },
    overrides: {
      MuiScopedCssBaseline: {
        root: {
          color: text,
        },
      },
      MuiButton: {
        root: {
          minHeight: 32,
          height: 32,
          padding: "0 20px",
          "&$disabled": {
            opacity: 0.6,
          },
          "&$focusVisible": {
            boxShadow: `0 0 8px ${primaryColor} !important`,
          },
          "&$textSizeSmall": {
            height: 28,
          },
          "&$textSizeLarge": {
            height: 36,
          },
          "&$outlinedSizeSmall": {
            height: 28,
          },
          "&$outlinedSizeLarge": {
            height: 36,
          },
          "&$containedSizeSmall": {
            height: 28,
            padding: "0 16px",
          },
          "&$containedSizeLarge": {
            height: 36,
            padding: "0 24px",
          },
        },
      },
      MuiCheckbox: {
        root: {
          width: 32,
          height: 32,
          padding: 0,
          "&.Mui-focusVisible .MuiSvgIcon-root": {
            borderRadius: 4,
            boxShadow: `0 0 8px ${primaryColor} !important`,
          },
        },
      },
      MuiRadio: {
        root: {
          width: 32,
          height: 32,
          padding: 0,
          "&.Mui-focusVisible .MuiSvgIcon-root": {
            borderRadius: "50%",
            boxShadow: `0 0 8px ${primaryColor} !important`,
          },
        },
      },
      MuiSwitch: {
        root: {
          width: 44,
          height: 32,
          padding: "4px 0",
          overflow: "initial",
        },
        track: {
          boxSizing: "border-box",
          borderRadius: 12,
          background: `${bg[300]} !important`,
          opacity: "1 !important",
          border: `1px solid ${border[500]}`,
        },
        thumb: {
          width: 16,
          height: 16,
          background: `${primaryColor} !important`,
          boxShadow: "none !important",
        },
        switchBase: {
          padding: "8px 4px",
          "&.Mui-checked.MuiSwitch-colorPrimary + .MuiSwitch-track": {
            background: `${primaryColor} !important`,
            border: "1px solid transparent",
          },
          "&.Mui-focusVisible + .MuiSwitch-track": {
            boxShadow: `0 0 8px ${primaryColor} !important`,
          },
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: "0.6 !important",
          },
          "&.Mui-checked.MuiSwitch-colorSecondary + .MuiSwitch-track": {
            background: `${secondaryColor} !important`,
            border: "1px solid transparent",
          },
          "&.Mui-checked  .MuiSwitch-thumb": {
            background: `${bg[50]} !important`,
          },
        },
        sizeSmall: {
          width: 40,
          height: 28,
          padding: "4px 0",
          "& .MuiSwitch-thumb": {
            width: 14,
            height: 14,
          },
          "& .MuiSwitch-switchBase": {
            padding: "7px 6px 7px 5px",
          },
        },
      },
      MuiChip: {
        root: {
          background: `${bg[400]} !important`,
        },
      },
      MuiPaginationItem: {
        root: {
          "&$focusVisible": {
            boxShadow: `0 0 8px ${primaryColor} !important`,
          },
        },
      },
      MuiTableRow: {
        root: {
          "&$selected": {
            background: fade(primaryColor, 0.1),
            "&:hover": {
              background: fade(primaryColor, 0.2),
            },
          },
        },
      },
      MuiDialog: {
        paper: {
          margin: 16,
          background: dark ? bg[300] : high,
        },
        paperFullWidth: {
          width: "calc(100% - 32px)",
        },
      },
      MuiListItem: {
        root: {
          "&$selected": {
            background: fade(primaryColor, 0.1),
            "&:hover": {
              background: fade(primaryColor, 0.2),
            },
          },
        },
      },
      MuiListItemIcon: {
        root: {
          minWidth: 36,
        },
      },
    },
  });
}

export default createTheme;
